import {
  DomainTypeEnum,
  TranslateLanguageTypeEnum,
  TranslationTypeEnum,
} from "@/entities/translation/translation.types"
import { Selectable } from "@beringlab/ui"
import { TFunction } from "i18next"
import { CurrencyEnum } from "@/entities/payment/payment.types"
import { Project, ProjectStatusEnum, UploadProject } from "./project.types"

export enum ProjectQueryKeysEnum {
  FetchProjectList = "fetchProjectList",
  FetchOwnershipInfo = "fetchOwnershipInfo",
  FetchProjectDetail = "fetchProjectDetail",
  FetchUnitRateList = "fetchUnitRateList",
  FetchAnonymousProject = "FetchAnonymousProject",
}

export const DEFAULT_PROJECT_FORM_VALUE: Project = {
  id: "",
  email: "",
  projectName: "",
  companyName: "",
  userName: "",
  createDt: "",
  expectedDeliveryDate: "",
  actionItems: [],
  note: "",
  translationType: TranslationTypeEnum.Mtpe,
  status: ProjectStatusEnum.QuotationInProgress,
  domain: DomainTypeEnum.Legal,
}

export const DEFAULT_UPLOAD_PROJECT_TYPE: UploadProject = {
  actionItems: [],
  expectedDeliveryDate: new Date().toISOString(),
  note: "",
  translationType: TranslationTypeEnum.Mtpe,
  currency: CurrencyEnum.Krw,
}

export const getProjectLanguageList = (
  t: TFunction
): Selectable<TranslateLanguageTypeEnum>[] => {
  return [
    { label: t("common.korean"), value: TranslateLanguageTypeEnum.Ko },
    { label: t("common.japanese"), value: TranslateLanguageTypeEnum.Ja },
    { label: t("common.english"), value: TranslateLanguageTypeEnum.En },
    { label: t("common.chinese2"), value: TranslateLanguageTypeEnum.Zh },
  ]
}

export const getInflowList = (t: TFunction): Selectable<string>[] => {
  return [
    { label: t("projects.inflowNaver"), value: "naver" },
    { label: t("projects.inflowGoogle"), value: "google" },
    { label: t("projects.inflowLinkedIn"), value: "linkedin" },
    { label: t("projects.inflowSns"), value: "sns" },
    { label: t("projects.inflowReferral"), value: "referral" },
    { label: t("projects.inflowEtc"), value: "etc" },
  ]
}

export const STEP_LABEL_KEYS = [
  "quotation.quotationCompleted",
  "quotation.translationInProgress",
  "quotation.translationDelivered",
  "quotation.paymentReceived",
] as const

export const statusConfig = {
  [ProjectStatusEnum.QuotationInProgress]: {
    bars: ["bg-key-color", "bg-gray-color/15", "bg-gray-color/15", "bg-gray-color/15"],
    texts: ["text-key-color", "text-disabled", "text-disabled", "text-disabled"],
    labels: ["quotation.quotationInProgress" as const, STEP_LABEL_KEYS[1], STEP_LABEL_KEYS[2], STEP_LABEL_KEYS[3]],
    partialIndex: 0,
  },
  [ProjectStatusEnum.QuotationCompleted]: {
    bars: ["bg-key-color", "bg-gray-color/15", "bg-gray-color/15", "bg-gray-color/15"],
    texts: ["text-key-color", "text-disabled", "text-disabled", "text-disabled"],
    labels: STEP_LABEL_KEYS,
  },
  [ProjectStatusEnum.TranslationInProgress]: {
    bars: ["bg-key-color", "bg-key-color", "bg-gray-color/15", "bg-gray-color/15"],
    texts: ["text-key-color", "text-key-color", "text-disabled", "text-disabled"],
    labels: STEP_LABEL_KEYS,
  },
  [ProjectStatusEnum.TranslationDelivered]: {
    bars: ["bg-key-color", "bg-key-color", "bg-key-color", "bg-gray-color/15"],
    texts: ["text-key-color", "text-key-color", "text-key-color", "text-disabled"],
    labels: STEP_LABEL_KEYS,
  },
  [ProjectStatusEnum.PaymentReceived]: {
    bars: ["bg-secondary", "bg-secondary", "bg-secondary", "bg-secondary"],
    texts: ["text-secondary", "text-secondary", "text-secondary", "text-secondary"],
    labels: STEP_LABEL_KEYS,
  },
  [ProjectStatusEnum.OrderCancelled]: {
    bars: ["bg-gray-backdrop-900", "bg-gray-color/15", "bg-gray-color/15", "bg-gray-color/15"],
    texts: ["text-gray-backdrop-900", "text-disabled", "text-disabled", "text-disabled"],
    labels: ["quotation.orderCancelled" as const, STEP_LABEL_KEYS[1], STEP_LABEL_KEYS[2], STEP_LABEL_KEYS[3]],
  },
  [ProjectStatusEnum.QuotationFailed]: {
    bars: ["bg-danger", "bg-gray-color/15", "bg-gray-color/15", "bg-gray-color/15"],
    texts: ["text-danger", "text-disabled", "text-disabled", "text-disabled"],
    labels: ["quotation.quotationFailed" as const, STEP_LABEL_KEYS[1], STEP_LABEL_KEYS[2], STEP_LABEL_KEYS[3]],
  },
}

/** @deprecated Use statusConfig instead */
export const statusColors = Object.fromEntries(
  Object.entries(statusConfig).map(([key, value]) => [key, value.bars])
) as Record<ProjectStatusEnum, string[]>

export const languageLabel = (
  t: TFunction,
  inputLanguage: TranslateLanguageTypeEnum
) => {
  return getProjectLanguageList(t).find(
    (language) => language.value === inputLanguage
  )?.label
}

export const getProjectDetailInfo = ({ label, value }: Selectable<string>) => {
  return (
    <section className="flex items-center justify-between">
      <p className="text-xs font-semibold text-disabled">{label}</p>
      <p className="text-sm text-gray-backdrop-900">{value}</p>
    </section>
  )
}
